import { TFileMessageOutputDto } from "@/api/services/main/conversation/dtos/create-message.dto";
import { IconChatReceived } from "@/assets/icons/IconChatReceived";
import { IconChatSent } from "@/assets/icons/IconChatSent";
import { IconDownload } from "@/assets/icons/IconDownload";
import { IconTrash } from "@/assets/icons/IconTrash";
import { Avatar } from "@/components/elements/Avatar";
import { IconBox } from "@/components/elements/IconBox";
import { MIME_TYPES } from "@/constants/file";
import { MessageFileInfo } from "@/features/chat/components/MessageFileInfo";
import { PreviewModal } from "@/features/chat/components/PreviewModal";
import { background, red, text } from "@/theme/colors";
import { elevation, rounded } from "@/theme/variables";
import { downloadFileFromPath } from "@/utils/export";
import { Box, Stack, styled, Typography } from "@mui/material";
import Linkify from "linkify-react";
import { FC, useState } from "react";

export type TMessageBoxProps = {
  id: number;
  readUserIds: number[];
  position: "left" | "right";
  title: string;
  messageText: string;
  statusTitle: string;
  status: "received" | "sent";
  files?: TFileMessageOutputDto[] | null;
  createdAt: string;
  handleRemoveMessage: (id: number) => Promise<void>;
};

export const MessageBox: FC<TMessageBoxProps> = ({ id, position, messageText, title, statusTitle, files, status, handleRemoveMessage }) => {
  const [isHovered, setIsHovered] = useState<boolean>(false);

  const [previewImageUrl, setPreviewImageUrl] = useState<string>();

  const colorText = position === "left" ? text.primary : text.white;

  return (
    <>
      <MessageBoxWrapper key={id} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
        <Stack flexDirection="row" justifyContent={position === "left" ? "flex-start" : "flex-end"} mt={2}>
          <Stack
            gap={0.5}
            maxWidth="90%"
            position="relative"
            boxShadow={elevation.lv4}
            borderRadius={rounded.sm}
            bgcolor={position === "left" ? background.primary : background.main}
            sx={{ borderTopLeftRadius: position === "left" ? "0px" : undefined, borderTopRightRadius: position === "right" ? "0px" : undefined }}
            p={1}
          >
            {isHovered && position === "right" && (
              <IconBox
                sx={{
                  position: "absolute",
                  top: "50%",
                  transform: "translateY(-50%)",
                  left: position === "right" ? "-30px" : undefined,
                }}
                onClick={async () => await handleRemoveMessage(id)}
              >
                <IconTrash color={red[70]} />
              </IconBox>
            )}
            {title && <Typography variant="body14Bold">{title}</Typography>}
            <Typography
              component="p"
              variant="body14"
              color={position === "left" ? text.primary : text.white}
              whiteSpace="pre-line"
              sx={{ wordBreak: "break-word" }}
            >
              <Linkify
                options={{
                  className: `linkify-text ${position === "left" ? "text-primary" : "text-white"}`,
                  target: "_blank",
                  rel: "noopener noreferrer",
                }}
              >
                {messageText}
              </Linkify>
            </Typography>
            <Stack alignItems="flex-start" gap={1}>
              {files &&
                files.map((file, index) => {
                  if (MIME_TYPES.IMAGE.some((item) => item.includes(file.mimeType))) {
                    return (
                      <Stack flexDirection="row" alignItems="center" gap={1} key={index}>
                        <Box sx={{ "&:hover": { opacity: 0.7, cursor: "pointer" } }} onClick={() => setPreviewImageUrl(file.signedUrl)}>
                          <Avatar imageSrc={file.signedUrl} width={150} height={150} />
                        </Box>
                        <IconBox onClick={async () => await downloadFileFromPath(file.filePath)}>
                          <IconDownload color={colorText} />
                        </IconBox>
                      </Stack>
                    );
                  }
                  return (
                    <Stack flexDirection="row" alignItems="center" gap={1} key={index}>
                      <MessageFileInfo file={file} side={position} color={colorText} />
                      <IconBox onClick={async () => await downloadFileFromPath(file.filePath)}>
                        <IconDownload color={colorText} />
                      </IconBox>
                    </Stack>
                  );
                })}
            </Stack>
            {statusTitle && (
              <Typography component="p" variant="cap12" textAlign="right" sx={{ verticalAlign: "middle" }} color={text.tertiary}>
                {`${statusTitle} `}
                {status === "received" && <IconChatReceived />}
                {status === "sent" && <IconChatSent />}
              </Typography>
            )}
          </Stack>
        </Stack>
      </MessageBoxWrapper>

      <PreviewModal url={previewImageUrl} isOpen={!!previewImageUrl} onClose={() => setPreviewImageUrl(undefined)} />
    </>
  );
};

const MessageBoxWrapper = styled(Box)`
  .text-primary {
    color: ${text.primary};
  }
  .text-white {
    color: ${text.white};
  }
  .linkify-text {
    word-break: break-all;
    text-decoration: underline;
  }
  &:last-child {
    padding-bottom: 4px;
  }
`;
